// LicenseGate: the ONLY place that renders license-state UI and derives the
// exportGate handed to the editor. States (per the plan):
//   trialing → full app, slim banner "X days left in trial" + Upgrade
//   active   → full app, no banner
//   grace    → full app, subtle "reconnect" notice
//   expired/canceled/revoked/no token → view-only mode (Export disabled)

import type { ReactNode } from 'react';
import { canExport, useLicense } from './LicenseProvider';
import { trialDaysLeft, type LicenseClaims } from '@imagelayerdecomposer/shared';
import { i18n } from '../ui/i18n';

export type ExportGate = { canExport: boolean; watermark: boolean };

export function gateFor(state: ReturnType<typeof useLicense>): ExportGate {
  return {
    canExport: canExport(state),
    watermark: Boolean(state.claims?.entitlements.watermark),
  };
}

/** Decides which banner to show (null = none). */
export function bannerFor(state: {
  phase: string;
  claims: LicenseClaims | null;
  grace: boolean;
}): {
  text: string;
  tone: 'warn' | 'success' | 'danger';
  upgrade?: boolean;
} | null {
  const c = state.claims;
  if (state.phase === 'unconfigured')
    return { text: 'Development mode — licensing is not configured (VITE_SUPABASE_URL missing)', tone: 'warn' };
  if (state.phase === 'loading') return null;
  if (!c) return { text: i18n.t('banner.expired_sub'), tone: 'danger', upgrade: true };
  if (state.grace) return { text: i18n.t('banner.grace'), tone: 'warn' };
  if (c.status === 'trialing') {
    // prefer the trial window (tee) over the 24h token expiry
    const iso = new Date((c.tee ?? c.exp) * 1000).toISOString();
    const days = trialDaysLeft(iso, Date.now());
    return { text: i18n.t('banner.trial', { n: days }), tone: 'warn', upgrade: true };
  }
  if (c.status === 'active') return null;
  return { text: i18n.t('banner.expired_sub'), tone: 'danger', upgrade: true };
}

/**
 * Slim banner shown UNDER the toolbar by the editor itself.
 */
export function LicenseBanner() {
  const state = useLicense();
  const banner = bannerFor(state);
  if (!banner) return null;
  return (
    <div className={`banner ${banner.tone}`} role="status" aria-live="polite">
      <span>{banner.text}</span>
      {banner.upgrade && (
        <button className="btn primary banner-btn" onClick={() => alert('Yükseltme: yöneticinizle iletişime geçin.')}>
          {i18n.t('cta.upgrade')}
        </button>
      )}
    </div>
  );
}

/** Convenience: the Banner wrapped when the layout is not under the App shell. */
export function LicenseGate({ children }: { children: ReactNode }) {
  const state = useLicense();
  const banner = bannerFor(state);
  return (
    <>
      {banner && (
        <div className={`banner ${banner.tone}`} role="status" aria-live="polite">
          <span>{banner.text}</span>
          {banner.upgrade && (
            <button className="btn primary banner-btn" onClick={() => alert('Yükseltme: yöneticinizle iletişime geçin.')}>
              {i18n.t('cta.upgrade')}
            </button>
          )}
        </div>
      )}
      {children}
    </>
  );
}
